<template>
  <div class="pb-page bv">
    <PageHeader title="Brands" :subtitle="subtitle">
      <template v-if="can('brands')">
        <LockedAction v-if="addCap" feature="brands" look="pb-button" icon="mdi-plus" label="Add client brand" :cap="addCap" :used="activeCount" />
        <button v-else type="button" class="pb-button" @click="openAdd"><i class="mdi mdi-plus pb-ico" aria-hidden="true"></i>Add client brand</button>
      </template>
    </PageHeader>

    <!-- Until the workspace has loaded, a skeleton: never "Add your first client" over brands that exist. -->
    <div v-if="loadFailed" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i>
      <span>{{ $store.getters['praiseboard/loadError'] || 'We couldn\'t load your brands. Check your connection and try again.' }}</span>
      <button type="button" class="pb-button sm outline" @click="refreshWorkspace">Retry</button></div>
    <SkeletonList v-else-if="!ready" :rows="3" variant="tiles" label="Loading your brands…" />
    <EmptyState v-else-if="!allBrands.length" icon="mdi-domain" title="Add your first client" text="Each brand gets its own collection pages, inbox, widgets and hosted wall.">
      <button type="button" class="pb-button" @click="openAdd">Add client brand</button>
    </EmptyState>

    <div v-else class="bv-grid">
      <article v-for="item in shownBrands" :key="item.id" :class="['pb-card', 'bv-card', { archived: item.status !== 'active' }]">
        <div class="bv-top">
          <img v-if="logoOf(item)" :src="logoOf(item)" alt="" class="bv-logo">
          <span v-else class="bv-mono" :style="{ background: item.color, color: onColor(item.color) }" aria-hidden="true">{{ initials(item.name) }}</span>
          <div class="bv-name-wrap">
            <h2 class="bv-name">{{ item.name }}</h2>
            <p class="pb-muted">{{ item.status === 'active' ? (item.wall_status === 'live' ? 'Wall live' : 'Wall not live') : 'Archived' }}<template v-if="item.admin_paused"> · paused by an administrator</template></p>
          </div>
        </div>
        <p v-if="item.offline" class="bv-offline"><span class="pb-chip is-warning"><i class="mdi mdi-eye-off-outline pb-ico" aria-hidden="true"></i>Public pages off</span>
          <span class="pb-muted">{{ item.offline }}</span></p>
        <dl class="bv-counts">
          <div><dt>New</dt><dd>{{ count(item, 'new') }}</dd></div>
          <div><dt>Awaiting client</dt><dd>{{ count(item, 'awaiting_client') }}</dd></div>
          <div><dt>Published</dt><dd>{{ count(item, 'published') }}</dd></div>
        </dl>
        <div class="pb-actions">
          <button v-if="item.status === 'active'" type="button" class="pb-button sm" @click="openInbox(item)">Open inbox</button>
          <router-link :to="'/brands/' + item.id" class="pb-button sm outline">Settings</router-link>
          <button v-if="item.status !== 'active' && can('brands')" type="button" class="pb-button sm outline" @click="restore(item)">Restore</button>
        </div>
      </article>
    </div>
    <button v-if="archivedCount" type="button" class="pb-button ghost bv-archived" @click="showArchived = !showArchived">{{ showArchived ? 'Hide archived' : 'Show ' + archivedCount + ' archived' }}</button>
    <!-- A plan with one brand: the client brands a higher plan runs, in their place. -->
    <LockedPanel v-if="ready && clientBrandsLocked" feature="brands" title="Client brands" :text="clientBrandsText" class="bv-locked" />

    <v-dialog v-model="addOpen" max-width="520">
      <form class="pb-dialog" @submit.prevent="add">
        <div class="pb-dialog-head"><h2>Add a client brand</h2>
          <button type="button" class="pb-icon-button" aria-label="Close" @click="addOpen = false"><i class="mdi mdi-close" aria-hidden="true"></i></button></div>
        <div class="pb-field"><label class="pb-label" for="bv-name">Brand name</label><input id="bv-name" v-model="form.name" class="pb-input" maxlength="120" required></div>
        <div class="pb-field"><label class="pb-label" for="bv-site">Website <span class="optional">(optional)</span></label><input id="bv-site" v-model="form.website_url" class="pb-input" maxlength="500" placeholder="client.com"></div>
        <div class="pb-field"><span id="bv-color-l" class="pb-label">Colour</span>
          <div class="bv-swatches" role="radiogroup" aria-labelledby="bv-color-l">
            <button v-for="swatch in swatches" :key="swatch.hex" type="button" role="radio" :aria-checked="form.color === swatch.hex ? 'true' : 'false'" :aria-label="swatch.name"
                    :class="['bv-swatch', { on: form.color === swatch.hex }]" :style="{ background: swatch.hex }" @click="form.color = swatch.hex"></button>
          </div></div>
        <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
        <div class="pb-dialog-actions">
          <button type="button" class="pb-button ghost" @click="addOpen = false">Cancel</button>
          <button type="submit" class="pb-button" :disabled="busy || !form.name.trim()">{{ busy ? 'Adding…' : 'Add brand' }}</button>
        </div>
      </form>
    </v-dialog>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import LockedAction from '../components/LockedAction.vue';
import LockedPanel from '../components/LockedPanel.vue';
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { post, message, planLimit } from '../api';
import { SWATCHES, initials, onColor } from '../lib/color';
import { number } from '../lib/format';
import { toast } from '../lib/toast';
import { limitReached } from '../lib/upgrades';
import { imageUrl } from '../publicUrls';

export default {
  name: 'BrandsView',
  components: { EmptyState, LockedAction, LockedPanel, PageHeader, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ showArchived: false, addOpen: false, busy: false, error: '', form: { name: '', website_url: '', color: '#2B5F8A' },
    swatches: SWATCHES, upgrade: null, upgradeOpen: false }),
  computed: {
    ready() { return this.$store.getters['praiseboard/loaded'] && !!this.ws && !!this.ws.workspace; },
    loadFailed() { return this.$store.getters['praiseboard/failed'] && !this.ws; },
    allBrands() { return (this.ws && this.ws.brands) || []; },
    shownBrands() { return this.allBrands.filter(item => this.showArchived || item.status === 'active'); },
    archivedCount() { return this.allBrands.filter(item => item.status !== 'active').length; },
    activeCount() { return this.allBrands.filter(item => item.status === 'active').length; },
    addCap() { return this.capOf('brands', this.activeCount); },
    clientBrandsLocked() { return (this.limits.brands || 1) <= 1 && !!this.availableOn('brands'); },
    clientBrandsText() {
      const plan = this.availableOn('brands');
      return this.planName(plan) + ' manages up to ' + this.planMatrix[plan].limits.brands
        + ' client brands, each with its own forms, widgets, hosted wall and client approvals.';
    },
    subtitle() {
      if (!this.ready) return '';
      const active = this.allBrands.filter(item => item.status === 'active').length;
      // A teammate with access to chosen brands sees only those, not the workspace's count.
      const member = this.ws && this.ws.member;
      if (member && member.brand_scope === 'selected') {
        return 'The ' + (active === 1 ? 'brand' : active + ' brands') + ' shared with you. Each has its own forms, inbox, widgets and wall.';
      }
      const allowed = this.limits.brands;
      const each = ' Each has its own forms, inbox, widgets and wall.';
      if (allowed && active > allowed) return active + ' active brands; your plan includes ' + allowed + '.' + each;
      return active + (allowed ? ' of ' + allowed : '') + ' active brand' + (active === 1 && !allowed ? '' : 's') + '.' + each;
    }
  },
  async mounted() { await this.requireWorkspace(); },
  methods: {
    initials,
    onColor,
    logoOf(item) { return item.logo && item.logo.status === 'complete' ? imageUrl(item.logo.key) : ''; },
    count(item, key) { return number((item.counts && item.counts[key]) || 0); },
    openAdd() {
      const reached = limitReached('brands', this.limits, this.brands.length, this.availableOn('brands'));
      if (reached) { this.upgrade = reached; this.upgradeOpen = true; return; }
      this.error = '';
      this.form = { name: '', website_url: '', color: '#2B5F8A' };
      this.addOpen = true;
    },
    fail(error) {
      const limit = planLimit(error);
      if (limit) { this.addOpen = false; this.upgrade = limit; this.upgradeOpen = true; return; }
      this.error = message(error);
    },
    async add() {
      this.busy = true;
      this.error = '';
      try {
        const brand = await post('/brands', { name: this.form.name.trim(), website_url: this.form.website_url.trim() || null, color: this.form.color });
        this.addOpen = false;
        await this.refreshWorkspace();
        this.$store.dispatch('praiseboard/selectBrand', brand.id);
        toast(brand.name + ' added. Create its first collection page next.');
        this.$router.push('/forms').catch(() => {});
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async restore(item) {
      try {
        await post('/brands/' + item.id + '/restore');
        await this.refreshWorkspace();
        toast(item.name + ' restored.');
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      }
    },
    openInbox(item) {
      this.$store.dispatch('praiseboard/selectBrand', item.id);
      this.$router.push('/inbox').catch(() => {});
    }
  }
};
</script>

<style scoped>
.bv-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr));gap:16px}
.bv-card{display:flex;flex-direction:column;gap:16px}
.bv-card.archived{opacity:.75;box-shadow:none}
.bv-top{display:flex;align-items:center;gap:12px}
.bv-logo{width:48px;height:48px;border-radius:12px;object-fit:contain;background:#fff;border:1px solid #E6DED2;flex:0 0 48px}
.bv-mono{display:inline-grid;place-items:center;width:48px;height:48px;border-radius:12px;font:700 17px var(--font-body);flex:0 0 48px}
.bv-name-wrap{min-width:0}
.bv-name{margin:0;font:600 17px/1.3 var(--font-body);color:#1F2328;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bv-offline{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;margin:-4px 0 14px}
.bv-counts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin:0}
.bv-counts div{padding:10px;border-radius:10px;background:#FBF8F3}
.bv-counts dt{font-size:12.5px;color:#6B635B}
.bv-counts dd{margin:2px 0 0;font:600 20px var(--font-body);color:#1F2328}
.bv-archived{margin-top:16px}
.bv-locked{margin-top:20px}
.bv-swatches{display:flex;flex-wrap:wrap;gap:8px}
.bv-swatch{width:44px;height:44px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 1px #D4C9B8;cursor:pointer}
.bv-swatch.on{box-shadow:0 0 0 2px #1F2328}
</style>
